import { DateTime } from "luxon";
import { array, date, object, string } from "yup";

import { Accession, Case } from "pwapi";

import {
  OLDEST_ACCEPTABLE_BIRTHDATE,
  OLDEST_COLLECTED_RECEIVED_DATE,
  OLDEST_BIRTHDATE_SANS_WARNING,
  YOUNGEST_ACCEPTABLE_BIRTHDATE,
  WORLDS_OLDEST_PERSONS_BIRTHDATE_DATETIME,
  OLDEST_HISTORICAL_COLLECTED_RECEIVED_DATE,
} from "./dateUtils";
import { emrFieldValidation } from "./validationUtils";

const validateAddress = (
  value: string | undefined,
  context: any,
  message: string
) => {
  if (
    !value &&
    context.options?.context?.lab?.settings.third_party_require_location_fields
      ?.active &&
    context.options?.context?.billing_type === Case.billing_type.GLOBAL
  ) {
    return context.createError({
      message,
    });
  }

  return true;
};

type TBDUser = {
  email?: string;
  first_name?: string;
  last_name?: string;
};

const validateTBD = (value: TBDUser | null | undefined, context: any) => {
  const tbdCheck = (v: string | undefined) => {
    return v && typeof v === "string" && v.toLowerCase().includes("tbd");
  };

  if (
    tbdCheck(value?.email) ||
    tbdCheck(value?.first_name) ||
    tbdCheck(value?.last_name)
  ) {
    return context.createError({
      message: 'Update any "TBD" users',
    });
  }

  return true;
};

const validateAddressState = (
  value: string | undefined,
  context: any,
  message: string
) => {
  const lab = context.options?.context?.lab;

  if (!value && lab?.settings?.billing_entry?.active) {
    return context.createError({
      message,
    });
  }

  return true;
};

export const getZonedWallTimeFromJSDate = (
  jsDate: Date,
  zone: string
): DateTime =>
  DateTime.fromObject(
    {
      year: jsDate.getFullYear(),
      month: jsDate.getMonth() + 1,
      day: jsDate.getDate(),
      hour: jsDate.getHours(),
      minute: jsDate.getMinutes(),
      second: jsDate.getSeconds(),
      millisecond: jsDate.getMilliseconds(),
    },
    { zone }
  );

const address = {
    phone1: string().test({
      name: "address.phone1",
      test: (value, context) => {
        const cleanedValue = value ? value.replace(/-/g, "") : value;
        return cleanedValue && cleanedValue.length < 10
          ? context.createError({
              message: "Phone 1 must be at least 12 characters",
            })
          : validateAddress(value, context, "Phone 1 is required");
      },
    }),
    phone2: string()
      .nullable()
      .test({
        name: "address.phone2",
        test: (value, context) => {
          const cleanedValue = value ? value.replace(/-/g, "") : value;
          return cleanedValue && cleanedValue.length < 10
            ? context.createError({
                message: "Phone 2 must be at least 12 characters",
              })
            : true;
        },
      }),
    street: string().test({
      name: "address.street",
      test: (value, context) =>
        validateAddress(value, context, "Street Address is required"),
    }),
    zipcode: string().test({
      name: "address.zipcode",
      test: (value, context) => {
        return value && value.length < 5
          ? context.createError({
              message: "Zipcode must be at least 5 characters",
            })
          : validateAddress(value, context, "Zipcode is required");
      },
    }),
  },
  billing_type = string().nullable().required("Billing type is required"),
  clinic = object().nullable().required("Clinic is required"),
  collected_date = date()
    .nullable()
    .required("Collected date is required")
    .when(["type", "case_type"], {
      is: (
        type: Case.type | null | undefined,
        case_type: Accession.case_type | null | undefined
      ) =>
        type === Case.type.HISTORICAL ||
        case_type === Accession.case_type.HISTORICAL,
      then: date()
        .nullable()
        .min(
          OLDEST_HISTORICAL_COLLECTED_RECEIVED_DATE,
          `Collected date must be after ${DateTime.fromISO(
            OLDEST_HISTORICAL_COLLECTED_RECEIVED_DATE
          ).toFormat("MMMM d, yyyy")}`
        ),
      otherwise: date()
        .nullable()
        .min(
          OLDEST_COLLECTED_RECEIVED_DATE,
          `Collected date must be after ${DateTime.fromISO(
            OLDEST_COLLECTED_RECEIVED_DATE
          ).toFormat("MMMM d, yyyy")}`
        ),
    })
    .test({
      test: (value, context) => {
        const tzCollected = context?.options?.context?.collected_date_timezone;
        const dtCollected =
          !!value && !!tzCollected
            ? getZonedWallTimeFromJSDate(value, tzCollected)
            : undefined;

        if (
          !!dtCollected &&
          dtCollected > DateTime.now().setZone(tzCollected)
        ) {
          return context.createError({
            message: "Collected date cannot be in the future",
          });
        }

        return true;
      },
    }),
  collected_date__warning = date()
    .nullable()
    .test({
      test: (value, context) => {
        if (
          context.options?.context?.case_type !== Accession.case_type.HISTORICAL
        ) {
          if (
            value &&
            DateTime.fromJSDate(new Date(value)) < YOUNGEST_ACCEPTABLE_BIRTHDATE
          ) {
            return context.createError({
              message: "Case was collected over a month ago",
            });
          }
        }

        return true;
      },
    }),
  external_ezderm_order_id = string()
    .nullable()
    .test({
      name: "external_ezderm_order_id",
      test: (value, context) =>
        emrFieldValidation(context, "external_ezderm_order_id", value),
    }),
  external_order_id = string()
    .nullable()
    .test({
      name: "external_order_id",
      test: (value, context) =>
        emrFieldValidation(context, "external_order_id", value),
    }),
  insurance = {
    insured_date_of_birth: date().test({
      name: "insurance.insured_date_of_birth",
      test: (value, context) => {
        if (value && DateTime.fromJSDate(new Date(value)) > DateTime.now()) {
          return context.createError({
            message: "Date of birth cannot be in the future",
          });
        }

        if (
          value &&
          DateTime.fromJSDate(new Date(value)) <
            WORLDS_OLDEST_PERSONS_BIRTHDATE_DATETIME
        ) {
          return context.createError({
            message: "Patient is over 115 years old",
          });
        }

        return true;
      },
    }),
  },
  insurance__accession = {
    insured_date_of_birth: date().test({
      name: "insurance.insured_date_of_birth",
      test: (value, context) => {
        if (value && DateTime.fromJSDate(new Date(value)) > DateTime.now()) {
          return context.createError({
            message: "Date of birth cannot be in the future",
          });
        }

        if (
          value &&
          DateTime.fromJSDate(new Date(value)) < OLDEST_ACCEPTABLE_BIRTHDATE
        ) {
          return context.createError({
            message: "Patient is over 115 years old",
          });
        }

        return true;
      },
    }),
  },
  insurance__warning = {
    insured_date_of_birth: date()
      .nullable()
      .test({
        name: "insurance.insured_date_of_birth",
        test: (value, context) => {
          if (!value) {
            return true;
          }

          const dateOfBirth = DateTime.fromJSDate(new Date(value));

          if (dateOfBirth > YOUNGEST_ACCEPTABLE_BIRTHDATE) {
            return context.createError({
              message: "Patient is less than a month old",
            });
          }

          if (
            dateOfBirth > WORLDS_OLDEST_PERSONS_BIRTHDATE_DATETIME &&
            dateOfBirth < OLDEST_BIRTHDATE_SANS_WARNING
          ) {
            return context.createError({
              message: "Patient is over 100 years old",
            });
          }

          return true;
        },
      }),
  },
  insurance__warning__accession = {
    insured_date_of_birth: date()
      .nullable()
      .test({
        name: "insurance.insured_date_of_birth",
        test: (value, context) => {
          if (!value) {
            return true;
          }

          const dateOfBirth = DateTime.fromJSDate(new Date(value));

          if (dateOfBirth > YOUNGEST_ACCEPTABLE_BIRTHDATE) {
            return context.createError({
              message: "Patient is less than a month old",
            });
          }

          if (
            dateOfBirth > OLDEST_ACCEPTABLE_BIRTHDATE &&
            dateOfBirth < OLDEST_BIRTHDATE_SANS_WARNING
          ) {
            return context.createError({
              message: "Patient is over 100 years old",
            });
          }

          return true;
        },
      }),
  },
  location_admin = {
    phone1: string().nullable().max(15, "Phone must be 15 characters or less"),
    phone2: string()
      .nullable()
      .max(15, "Phone 2 must be 15 characters or less"),
    zipcode: string()
      .nullable()
      .max(10, "Zip code must be 10 characters or less"),
  },
  ordering_doctor = object()
    .nullable()
    .required("Ordering provider is required"),
  ordering_doctor_admin = object()
    .nullable()
    .test({
      name: "ordering_doctor_admin",
      test: (value, context) => validateTBD(value, context),
    }),
  patient = (nestedPath?: boolean) => ({
    date_of_birth: date()
      .nullable()
      .required("Date of birth is required")
      .test({
        name: "patient.date_of_birth",
        test: (value, context) => {
          if (value && DateTime.fromJSDate(new Date(value)) > DateTime.now()) {
            return context.createError({
              message: "Date of birth cannot be in the future",
            });
          }

          if (value && context.options?.context?.collected_date) {
            const dob = DateTime.fromJSDate(new Date(value)).startOf("day");
            const collected = DateTime.fromJSDate(
              new Date(context.options.context.collected_date)
            ).startOf("day");

            if (dob > collected) {
              return context.createError({
                message: "D.O.B. must be on or before the collected date",
              });
            }
          }

          if (
            value &&
            DateTime.fromJSDate(new Date(value)) < OLDEST_ACCEPTABLE_BIRTHDATE
          ) {
            return context.createError({
              message: "Patient is over 115 years old",
              path: `${nestedPath ? "patient." : ""}date_of_birth`,
            });
          }

          return true;
        },
      }),
    external_patient_id: string()
      .nullable()
      .test({
        name: "patient.external_patient_id",
        test: (value, context) =>
          emrFieldValidation(
            context,
            `${nestedPath ? "patient." : ""}external_patient_id`,
            value
          ),
      }),
    first_name: string().required("First name is required"),
    last_name: string().required("Last name is required"),
    medical_record_number: string()
      .nullable()
      .test({
        name: "patient.medical_record_number",
        test: (value, context) =>
          emrFieldValidation(
            context,
            `${nestedPath ? "patient." : ""}medical_record_number`,
            value
          ),
      }),
    sex: string().required("Sex is required"),
  }),
  patient_admin = {
    date_of_birth: date()
      .nullable()
      .required("Date of birth is required")
      .test({
        name: "patient.date_of_birth",
        test: (value, context) => {
          if (value && DateTime.fromJSDate(new Date(value)) > DateTime.now()) {
            return context.createError({
              message: "Date of birth cannot be in the future",
            });
          }

          if (
            value &&
            DateTime.fromJSDate(new Date(value)) <
              WORLDS_OLDEST_PERSONS_BIRTHDATE_DATETIME
          ) {
            return context.createError({
              message: "Patient is over 115 years old",
            });
          }

          return true;
        },
      }),
    medical_record_number: string()
      .nullable()
      .max(20, "MRN must be 20 characters or less"),
    phone: string().nullable().max(15, "Phone must be 15 characters or less"),
    ssn: string()
      .nullable()
      .matches(/^[0-9]+$/, "SSN must be only digits")
      .min(9, "SSN must be exactly 9 digits")
      .max(9, "SSN must be exactly 9 digits"),
  },
  patient_guarantor_admin = {
    date_of_birth: date()
      .nullable()
      .test({
        name: "patient.date_of_birth",
        test: (value, context) => {
          if (context.options?.context?.relationship_to_patient === "SELF") {
            return true;
          }

          if (value && DateTime.fromJSDate(new Date(value)) > DateTime.now()) {
            return context.createError({
              message: "Date of birth cannot be in the future",
            });
          }

          if (
            value &&
            DateTime.fromJSDate(new Date(value)) <
              WORLDS_OLDEST_PERSONS_BIRTHDATE_DATETIME
          ) {
            return context.createError({
              message: "Patient is over 115 years old",
            });
          }

          return true;
        },
      }),
  },
  guarantor__warning = {
    address: object().when("relationship_to_patient", {
      is: (relation: string | null | undefined) =>
        relation === "SPOUSE" || relation === "PARENT",
      then: object()
        .nullable()
        .test("address", (value: any, context: any) => {
          const patientAddress = {
            street: "",
            street2: "",
            city: "",
            state: "",
            zipcode: "",
            ...(context.options.context?.address || {}),
          };

          const addressWarnings = Object.keys(patientAddress || {}).filter(
            (key) => {
              // Skip phone numbers and id comparison
              if (key === "phone1" || key === "phone2" || key === "id")
                return false;
              return (
                ((value || {})[key] || undefined) !==
                (patientAddress[key] || undefined)
              );
            }
          );

          if (addressWarnings.length > 0) {
            if (addressWarnings.length > 1)
              addressWarnings[addressWarnings.length - 1] = `and ${
                addressWarnings[addressWarnings.length - 1]
              }`;
            return context.createError({
              message: `Guarantor ${addressWarnings.join(
                addressWarnings.length > 2 ? ", " : " "
              )} differs from patient address`,
            });
          }
          return true;
        }),
    }),
  },
  patient__warning = (nestedPath?: boolean) => ({
    date_of_birth: date()
      .nullable()
      .transform((curr, orig) => (orig === "" ? null : curr))
      .test({
        name: "date_of_birth",
        test: (value, context) => {
          if (!value) {
            return true;
          }

          const dateOfBirth = DateTime.fromJSDate(new Date(value));

          if (dateOfBirth > YOUNGEST_ACCEPTABLE_BIRTHDATE) {
            return context.createError({
              message: "Patient is less than a month old",
              path: `${nestedPath ? "patient." : ""}date_of_birth`,
            });
          }

          if (
            dateOfBirth >= WORLDS_OLDEST_PERSONS_BIRTHDATE_DATETIME &&
            dateOfBirth < OLDEST_BIRTHDATE_SANS_WARNING
          ) {
            return context.createError({
              message: "Patient is over 100 years old",
              path: `${nestedPath ? "patient." : ""}date_of_birth`,
            });
          }

          return true;
        },
      }),
  }),
  received_date = () => {
    return date()
      .nullable()
      .when("status", {
        is: (caseStatus: Case.status | null | undefined) =>
          caseStatus !== Case.status.ORDERED,
        then: date()
          .nullable()
          .required("Received date is required")
          .when(["type", "case_type"], {
            is: (
              type: Case.type | null | undefined,
              case_type: Accession.case_type | null | undefined
            ) =>
              type === Case.type.HISTORICAL ||
              case_type === Accession.case_type.HISTORICAL,
            then: date()
              .nullable()
              .min(
                OLDEST_HISTORICAL_COLLECTED_RECEIVED_DATE,
                `Received date must be after ${DateTime.fromISO(
                  OLDEST_HISTORICAL_COLLECTED_RECEIVED_DATE
                ).toFormat("MMMM d, yyyy")}`
              ),
            otherwise: date()
              .nullable()
              .min(
                OLDEST_COLLECTED_RECEIVED_DATE,
                `Received date must be after ${DateTime.fromISO(
                  OLDEST_COLLECTED_RECEIVED_DATE
                ).toFormat("MMMM d, yyyy")}`
              ),
          })
          .test({
            test: (value, context) => {
              // Get Collected Date and Received Date with proper timezones
              const dateCollectedISO: string =
                context.options?.context?.collected_date;
              const tzCollected: string =
                context.options?.context?.collected_date_timezone;
              const dtCollected =
                !!dateCollectedISO && !!tzCollected
                  ? DateTime.fromISO(dateCollectedISO, { zone: tzCollected })
                  : undefined;

              const tzReceived: string =
                context.options?.context?.received_date_timezone;
              const dtReceived =
                !!value && !!tzReceived
                  ? getZonedWallTimeFromJSDate(value, tzReceived)
                  : undefined;

              // Verify Received Date is not in the future
              if (
                !!dtReceived &&
                dtReceived > DateTime.now().setZone(tzReceived)
              ) {
                return context.createError({
                  message: "Received date cannot be in the future",
                });
              }

              // Verify Received Date is after Collected Date
              if (!!dtCollected && !!dtReceived && dtReceived <= dtCollected) {
                return context.createError({
                  message: "Received date must be after the collected date",
                });
              }

              return true;
            },
          }),
      });
  },
  supervising_physician = object()
    .nullable()
    .test(
      "supervising_physician",
      "Supervising physician cannot be the same as ordering physician",
      (value, context) => {
        return !(value?.id && value.id === context.parent.ordering_doctor?.id);
      }
    ),
  supervising_physician_admin = object()
    .nullable()
    .test({
      name: "supervising_physician_admin",
      test: (value, context) => validateTBD(value, context),
    });

export const newAccessionSchema = object().shape({
  address: object().shape({
    ...address,
    // Potentially require address only at accessioning
    state: string().test({
      name: "address.state",
      test: (value, context) =>
        validateAddressState(value, context, "State is required"),
    }),
  }),
  billing_type,
  clinic,
  collected_date,
  external_ezderm_order_id,
  external_order_id,
  insurance: array().of(object().shape(insurance__accession)),
  ordering_doctor,
  patient: object().shape(patient(true)),
  received_date: received_date(),
  supervising_physician,
});

export const caseSchema = object().shape({
  collected_date,
  received_date: received_date(),
});

export const caseWarningSchema = object().shape({
  finalizing_signature: object()
    .nullable()
    .test({
      name: "finalizing_signature",
      test: (value, context) => validateTBD(value?.user, context),
    }),
});

export const newAccessionWarningSchema = object().shape({
  collected_date: collected_date__warning,
  patient: object().shape(patient__warning(true)),
  insurance: array().of(object().shape(insurance__warning__accession)),
  guarantor: array().nullable().of(object().shape(guarantor__warning)),
});

export const integrationSchema = object().shape({
  clinic,
  external_ezderm_order_id,
  external_order_id,
  external_patient_id: patient().external_patient_id,
  ordering_doctor,
  supervising_physician,
});

export const integrationAdminSchema = object().shape({
  clinic,
  ordering_doctor,
  supervising_physician,
});

export const integrationAdminWarningSchema = object().shape({
  ordering_doctor: ordering_doctor_admin,
  supervising_physician: supervising_physician_admin,
});

export const patientSchema = object().shape({
  address: object().shape(address),
  date_of_birth: patient().date_of_birth,
  external_patient_id: patient().external_patient_id,
  first_name: patient().first_name,
  last_name: patient().last_name,
  medical_record_number: patient().medical_record_number,
  sex: patient().sex,
});

export const patientAdminSchema = object().shape(patient_admin);

export const patientGuarantorAdminSchema = object().shape({
  ...patient_guarantor_admin,
  address: object().nullable().shape(location_admin),
  phone: patient_admin.phone,
  ssn: patient_admin.ssn,
});
export const patientGuarantorWarningSchema = object().shape({
  ...guarantor__warning,
  ...patient__warning(),
});
export const locationAdminSchema = object().shape(location_admin);

export const patientWarningSchema = object().shape(patient__warning());

export const insuranceSchema = object().shape(insurance);

export const insuranceWarningSchema = object().shape(insurance__warning);

export const caseDetailsSchema = object().shape({
  billing_type,
  collected_date,
  received_date: received_date(),
});

export const caseDetailsWarningSchema = object().shape({
  collected_date: collected_date__warning,
});
